/**
 * @file
 * @fileOverview This file contains the CustomToggle class for the Phaser scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

/**
 * CustomToggle class extending Phaser.GameObjects.Container
 * @class
 * @extends Phaser.GameObjects.Container
 * @classdesc A customizable toggle button class for Phaser games with on/off states and various interactive features.
 */
class CustomToggle extends Phaser.GameObjects.Container {
    /**
   * Creates a new CustomToggle instance.
   * @constructor
   * @param {Phaser.Scene} scene - The Scene to which this toggle belongs.
   * @param {string} id - Unique identifier for the toggle.
   * @param {number} x - The x position of the toggle.
   * @param {number} y - The y position of the toggle.
   * @param {Function} callback - Function to call when the toggle state changes.
   * @param {Object} [options={}] - Additional options for customizing the toggle.
   * @param {boolean} [options.initialState=false] - Initial state of the toggle (true for on, false for off).
   * @param {number} [options.width=60] - Width of the toggle.
   * @param {number} [options.height=30] - Height of the toggle.
   * @param {number} [options.onColor=0x4caf50] - Color when the toggle is on.
   * @param {number} [options.offColor=0xff0000] - Color when the toggle is off.
   * @param {number} [options.knobColor=0xffffff] - Color of the toggle knob.
   * @param {number} [options.borderColor=0x0000ff] - Color of the toggle border.
   * @param {number} [options.borderWidth=2] - Width of the toggle border.
   */
  constructor(scene, id, x, y, callback, options = {}) {
    super(scene, x, y);
    this.id = id;
    this.isOn = options.initialState || false;
    this.callback = callback;
    // Default options
    this.options = {
      width: options.width || 60,
      height: options.height || 30,
      onColor: options.onColor || 0x4caf50,
      offColor: options.offColor || 0xff0000,
      knobColor: options.knobColor || 0xffffff,
      borderColor: options.borderColor || 0x0000ff,
      borderWidth: options.borderWidth || 2,
      ...options,
    };

    // Background
    this.background = scene.add.rectangle(
      0,
      0,
      this.options.width,
      this.options.height,
      this.isOn ? this.options.onColor : this.options.offColor
    );
    this.background.setOrigin(0.5);

    // Border
    this.border = scene.add.rectangle(
      0,
      0,
      this.options.width + this.options.borderWidth,
      this.options.height + this.options.borderWidth
    );
    this.border.setStrokeStyle(
      this.options.borderWidth,
      this.options.borderColor
    );
    this.border.setOrigin(0.5);

    // Knob
    this.knob = scene.add.circle(
      this.isOn ? this.options.width / 4 : -this.options.width / 4,
      0,
      this.options.height / 2 - 4,
      this.options.knobColor
    );
    this.knob.setOrigin(0.5);

    // Add to container
    this.add(this.border);
    this.add(this.background);
    this.add(this.knob);

    // Enable input
    this.setSize(this.options.width, this.options.height);
    this.setInteractive(
      new Phaser.Geom.Rectangle(
        -this.options.width / 2,
        -this.options.height / 2,
        this.options.width,
        this.options.height
      ),
      Phaser.Geom.Rectangle.Contains
    )
      .on('pointerdown', this.toggle.bind(this))
      .on('pointerup', () => {
        //callback(this.isOn);
      });

    this.input.useHandCursor = true;
    this.input.alwaysEnabled = true;
    this.input.cursor = true;

    scene.add.existing(this);
  }

   /**
   * Get the unique identifier of the toggle.
   * @returns {string} The toggle's ID.
   */ 
  getId() {
    return this.id;
  }

  /**
   * Toggle the state of the button.
   * This method changes the visual appearance of the toggle and calls the callback function.
   */
  toggle() {
    this.isOn = !this.isOn;
    this.background.setFillStyle(
      this.isOn ? this.options.onColor : this.options.offColor
    );
    this.knob.setX(
      this.isOn ? this.options.width / 4 : -this.options.width / 4
    );
    this.callback(this.isOn);
  }

  setToggleState(state) {
    this.isOn = state;
    this.background.setFillStyle(
      this.isOn ? this.options.onColor : this.options.offColor
    );
    this.knob.setX(
      this.isOn ? this.options.width / 4 : -this.options.width / 4
    );
  }

  /**
   * Simulate a click event on the toggle.
   * @param {number} x - The x coordinate of the simulated click.
   * @param {number} y - The y coordinate of the simulated click.
   */
  simulateClick(x, y) {
    if (this.contains(x, y)) {
      const pointer = this.scene.input.activePointer;

      // Create pointerdown event
      this.emit('pointerdown', pointer, this);
      // Create pointerup event
      this.emit('pointerup', pointer, this);
    }
  }

  /**
   * Check if a point is within the bounds of the toggle.
   * @param {number} x - The x coordinate to check.
   * @param {number} y - The y coordinate to check.
   * @returns {boolean} True if the point is within the toggle's bounds, false otherwise.
   */
  contains(x, y) {
    const bounds = this.getBounds();
    return (
      x >= bounds.left &&
      x <= bounds.right &&
      y >= bounds.top &&
      y <= bounds.bottom
    );
  }

  /**
   * Set the visibility of the toggle and its child elements.
   * @param {boolean} isVisible - Whether the toggle should be visible or not.
   */
  setVisible(isVisible) {
    this.setVisible(isVisible);
    this.list.forEach((child) => child.setVisible(isVisible));
  }
}

export default CustomToggle;
